import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CalendarClock, Plus, FileText, CheckCircle2, AlertCircle, MoreHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Button } from '@verlo/shared/components/ui/button';
import { Switch } from '@verlo/shared/components/ui/switch';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@verlo/shared/components/ui/dropdown-menu';
import { reportsIntelApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { formatRange } from '../../../components/reports/formatters';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { OptionChip } from '@verlo/shared/components/ui/segmented';
import { nextRun, shortWhen, toWhom, whenWords } from '../../../lib/scheduleWords';

const TEMPLATES = [
  { value: 'executive', label: 'Executive snapshot' },
  { value: 'financial', label: 'Financial · P&L' },
  { value: 'stock',     label: 'Stock & Margin' },
  { value: 'labour',    label: 'Labour intelligence' },
];

// A report saved in the report builder. It goes out as saved, about the period the schedule
// picks: a view saved as "Today" would be empty at 08:00, so each frequency defaults to the
// period that has just finished.
const SAVED = { value: 'saved_view', label: 'A saved report' };
const PERIODS = ['yesterday', 'last_week', 'last_month', 'last_quarter', 'wtd', 'mtd'];
const PERIOD_FOR = { daily: 'yesterday', weekly: 'last_week', monthly: 'last_month', quarterly: 'last_quarter' };

const FREQUENCIES = [
  { value: 'daily',     label: 'Daily' },
  { value: 'weekly',    label: 'Weekly' },
  { value: 'monthly',   label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const emptyForm = {
  name: '',
  template: 'executive',
  frequency: 'weekly',
  day_of_week: 1,
  day_of_month: 1,
  time_hhmm: '08:00',
  recipients: '',
  format: 'pdf',
  enabled: true,
  view_id: '',
  period: 'last_week',
};

const ReportsSchedulesPage = () => {
  const [schedules, setSchedules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // the schedule the dialog is changing; null for a new one
  const [editingFrom, setEditingFrom] = useState(null); // that schedule as it was, to send only what changed
  const [confirmingDelete, setConfirmingDelete] = useState(false); // Delete, in the Edit dialog, asks first
  const [views, setViews] = useState([]);
  const [periodTouched, setPeriodTouched] = useState(false);
  const [params, setParams] = useSearchParams();
  const { user, hasPermission } = useAuth();
  const canSave = hasPermission('reports.view_builder');
  // A saved-report schedule opened by someone without the builder still names its kind.
  const templates = canSave || form.template === 'saved_view' ? [...TEMPLATES, SAVED] : TEMPLATES;
  const viewName = useMemo(() => Object.fromEntries(views.map((v) => [v.id, v.name])), [views]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        reportsIntelApi.listSchedules(),
        reportsIntelApi.listScheduleRuns(30),
      ]);
      setSchedules(s.data.schedules || []);
      setRuns(r.data.runs || []);
      if (canSave) setViews((await reportsIntelApi.listBuilderViews()).data.views || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // Arriving from the report builder's Schedule button (?view=<id>): open a new schedule
  // for that report, named after it, to the person setting it up.
  useEffect(() => {
    const vid = params.get('view');
    if (!vid || !views.length) return;
    const v = views.find((x) => x.id === vid);
    if (v) {
      setForm({ ...emptyForm, template: 'saved_view', view_id: v.id, name: v.name, recipients: user?.email || '' });
      setPeriodTouched(false);
      setDialogOpen(true);
    } else {
      toast.error('That saved report no longer exists');
    }
    setParams({}, { replace: true });
  }, [params, views]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFrequency = (frequency) => setForm((f) => ({
    ...f, frequency, period: periodTouched ? f.period : PERIOD_FOR[frequency],
  }));

  const pickView = (id) => setForm((f) => ({
    ...f, view_id: id, name: !f.name || f.name === viewName[f.view_id] ? viewName[id] : f.name,
  }));

  const onCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Schedule name is required');
      return;
    }
    if (form.template === 'saved_view' && !form.view_id) {
      toast.error('Pick the saved report to send');
      return;
    }
    setSaving(true);
    try {
      const saved = form.template === 'saved_view';
      const payload = {
        ...form,
        view_id: saved ? form.view_id : null,
        period: saved ? form.period : null,
        recipients: form.recipients
          .split(/[,;\s]+/)
          .map((x) => x.trim())
          .filter(Boolean),
      };
      if (editingId) {
        // Only what changed: re-sending an unchanged report would ask for the report builder.
        const changed = Object.fromEntries(Object.entries(payload)
          .filter(([k, v]) => JSON.stringify(v ?? null) !== JSON.stringify(editingFrom?.[k] ?? null)));
        await reportsIntelApi.updateSchedule(editingId, changed);
      } else await reportsIntelApi.createSchedule(payload);
      toast.success(editingId ? 'Schedule saved' : 'Schedule created');
      setDialogOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (editingId ? 'Could not save the schedule' : 'Could not create schedule'));
    } finally {
      setSaving(false);
    }
  };

  const onToggle = async (sched, enabled) => {
    setSchedules((ss) => ss.map((s) => (s.id === sched.id ? { ...s, enabled } : s)));
    try {
      await reportsIntelApi.updateSchedule(sched.id, { enabled });
    } catch {
      toast.error('Could not update schedule');
      loadAll();
    }
  };

  // Delete lives in the Edit dialog (Martin, 21 Oct: the row's menu is Edit and Download PDF).
  const onDelete = async () => {
    try {
      await reportsIntelApi.deleteSchedule(editingId);
      toast.success(`${form.name || 'Schedule'} deleted`);
      setDialogOpen(false);
      setEditingId(null);
      loadAll();
    } catch { toast.error("Couldn't delete it"); }
  };

  const onNew = () => { setEditingId(null); setForm(emptyForm); setPeriodTouched(false); setDialogOpen(true); };

  const onEdit = (sched) => {
    setEditingId(sched.id);
    setEditingFrom(sched);
    setConfirmingDelete(false);
    setForm({
      ...emptyForm,
      ...Object.fromEntries(Object.entries(sched).filter(([k]) => k in emptyForm)),
      view_id: sched.view_id || '',
      period: sched.period || PERIOD_FOR[sched.frequency] || 'last_week',
      recipients: (sched.recipients || []).join(', '),
    });
    setPeriodTouched(true);
    setDialogOpen(true);
  };


  const onDownload = async (sched) => {
    try {
      const res = await reportsIntelApi.downloadSchedulePdf(sched.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `verlo-${(sched.template === 'saved_view' ? sched.name : sched.template).toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF download failed');
    }
  };

  const templateLabel = (v) => (
    v === 'saved_view' ? 'Saved report' : v === 'before_service' ? 'Before Service email' : TEMPLATES.find((t) => t.value === v)?.label || v);

  // What happened to a run, in words. `queued` is handed to the mailer: sent, as far as anyone
  // here is told; `resend` is a run from before the email port, which recorded the provider.
  const runOutcome = (r) => {
    if (r.delivery_mode === 'failed') return { ok: false, words: 'Not delivered' };
    if (r.status === 'skipped' || r.delivery_mode === 'skipped') return { ok: false, words: 'Not sent' };
    if (r.delivery_mode === 'preview_only') return { ok: true, words: 'Made, not emailed (email is off here)' };
    if (['queued', 'resend', 'sent'].includes(r.delivery_mode)) return { ok: true, words: 'Sent' };
    return { ok: r.status === 'delivered', words: r.status === 'delivered' ? 'Made' : 'Not sent' };
  };

  return (
    <ReportsLayout>
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap" data-testid="schedules-toolbar">
        <p className="text-[14px] text-ink-soft">
          Reports sent by email on a regular day and time.
          {import.meta.env.VITE_RESEND_LIVE !== '1' && <span className="text-ink-faint"> Email isn’t switched on here, so they’re made but not sent.</span>}
        </p>
        <Button className="h-9 gap-1.5 bg-verlo hover:bg-verlo-hover" onClick={onNew} data-testid="schedules-create-btn">
          <Plus size={13} />
          New schedule
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
        {/* Schedules: one sentence for what, when and to whom; the switch pauses one without
            deleting it (Back office sweep, Oct 2026). */}
        <div className="xl:col-span-3 self-start bg-white border border-hairline rounded-lg overflow-hidden" data-testid="schedules-list">
          {loading ? (
            <div className="p-6 text-center text-[13px] text-ink-faint">Loading…</div>
          ) : schedules.length === 0 ? (
            <div className="p-10 text-center text-[13px] text-ink-soft" data-testid="schedules-empty">
              <CalendarClock size={28} className="mx-auto mb-2 text-ink-faint" />
              No schedules yet. <b>New schedule</b> sends a report by email on the day and time you pick.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-[minmax(0,2.6fr)_minmax(0,1.3fr)_56px_40px] gap-3.5 border-b border-hairline bg-surface-2 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                <span>Report</span><span>Next send</span><span>On</span><span />
              </div>
              <ul className="divide-y divide-hairline">
                {schedules.map((s) => {
                  const gone = s.template === 'saved_view' && !viewName[s.view_id];
                  const who = toWhom(s.recipients);
                  return (
                    <li key={s.id} className="grid grid-cols-[minmax(0,2.6fr)_minmax(0,1.3fr)_56px_40px] items-center gap-3.5 px-5 py-3.5 hover:bg-hover-tint" data-testid={`schedule-row-${s.id}`}>
                      <div className="min-w-0">
                        <p className="truncate text-[14.5px] font-semibold text-ink">{s.name}</p>
                        <p className="mt-0.5 text-[13px] text-ink-soft">
                          {whenWords(s)}
                          {who ? `, to ${who}` : <span className="text-signal-warn">, to nobody yet</span>}
                          {s.template === 'saved_view'
                            ? (gone
                              ? <span className="text-signal-warn"> · its saved report was deleted</span>
                              : <> · {viewName[s.view_id]}, {formatRange(s.period || 'as saved').toLowerCase()}</>)
                            : <> · {templateLabel(s.template)}</>}
                        </p>
                      </div>
                      <span className={`text-[13.5px] tabular-nums ${s.enabled ? 'text-ink' : 'text-ink-faint'}`} data-testid={`schedule-next-${s.id}`}>
                        {s.enabled ? shortWhen(nextRun(s)) : 'Paused'}
                      </span>
                      <Switch
                        checked={!!s.enabled}
                        onCheckedChange={(v) => onToggle(s, v)}
                        aria-label={`${s.name} is ${s.enabled ? 'on' : 'paused'}`}
                        data-testid={`schedule-toggle-${s.id}`}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="grid h-8 w-8 place-items-center rounded-md border border-hairline bg-white text-ink-soft hover:bg-hover-tint hover:text-ink"
                            aria-label={`More for ${s.name}`}
                            data-testid={`schedule-more-${s.id}`}
                          >
                            <MoreHorizontal size={15} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onSelect={() => onEdit(s)} data-testid={`schedule-edit-${s.id}`}>Edit</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => onDownload(s)} data-testid={`schedule-download-${s.id}`}>Download PDF</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {/* What went out lately, in words: what it was, what happened, when. */}
        <div className="xl:col-span-2 self-start bg-white border border-hairline rounded-lg overflow-hidden" data-testid="schedule-runs">
          <div className="px-5 py-3.5 border-b border-hairline">
            <h3 className="font-heading text-[15px] font-extrabold text-ink">Recent runs</h3>
            <p className="text-[12.5px] text-ink-soft">The last 30, Before Service emails included</p>
          </div>
          {runs.length === 0 ? (
            <div className="p-10 text-center text-[13px] text-ink-faint">
              <FileText size={26} className="mx-auto mb-2 text-ink-faint" />
              Nothing has run yet.
            </div>
          ) : (
            <ul className="divide-y divide-hairline max-h-[600px] overflow-y-auto">
              {runs.map((r) => {
                const outcome = runOutcome(r);
                return (
                  <li key={r.id} className="px-5 py-3 flex items-start gap-3" data-testid={`schedule-run-${r.id}`}>
                    <span className={`mt-0.5 shrink-0 ${outcome.ok ? 'text-signal-ok' : 'text-signal-stop'}`}>
                      {outcome.ok ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13.5px] font-semibold text-ink truncate">{r.schedule_name || templateLabel(r.template)}</p>
                      <p className="text-[12.5px] text-ink-soft mt-0.5">
                        {outcome.words} · {new Date(r.ran_at).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      {r.delivery_error && <p className="text-[12.5px] text-ink-faint mt-0.5">{r.delivery_error}</p>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* New schedule dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditingId(null); }}>
        <DialogContent className="max-w-lg" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? `Edit ${form.name || 'schedule'}` : 'New schedule'}</DialogTitle>
            <DialogDescription>
              Pick the report and when it goes. It’s made as a PDF and emailed to the people you list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="schedule-name" className="text-[12px]">Name</Label>
              <Input
                id="schedule-name"
                placeholder="e.g. Monday morning P&L"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="schedule-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[12px]">Template</Label>
                <Select value={form.template} onValueChange={(v) => setForm((f) => ({ ...f, template: v }))}>
                  <SelectTrigger data-testid="schedule-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px]">Format</Label>
                <Select value={form.format} onValueChange={(v) => setForm((f) => ({ ...f, format: v }))}>
                  <SelectTrigger data-testid="schedule-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF attachment</SelectItem>
                    <SelectItem value="html">HTML email body</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.template === 'saved_view' && (
              views.length ? (
                <div className="grid grid-cols-2 gap-3" data-testid="schedule-saved-fields">
                  <div className="space-y-1.5">
                    <Label className="text-[12px]">Saved report</Label>
                    <Select value={form.view_id} onValueChange={pickView}>
                      <SelectTrigger data-testid="schedule-view-select"><SelectValue placeholder="Pick one" /></SelectTrigger>
                      <SelectContent>
                        {views.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[12px]">Covering</Label>
                    <Select value={form.period} onValueChange={(v) => { setPeriodTouched(true); setForm((f) => ({ ...f, period: v })); }}>
                      <SelectTrigger data-testid="schedule-period-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PERIODS.map((p) => <SelectItem key={p} value={p}>{formatRange(p)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <p className="text-[12.5px] text-ink-soft" data-testid="schedule-no-saved">
                  You haven’t saved a report yet. Build one in the{' '}
                  <Link to="/back-office/reports/builder" className="font-semibold text-verlo-hover">report builder</Link>, save it, then schedule it here.
                </p>
              )
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[12px]">Frequency</Label>
                <Select value={form.frequency} onValueChange={setFrequency}>
                  <SelectTrigger data-testid="schedule-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="schedule-time" className="text-[12px]">Time (the venue’s clock)</Label>
                <Input
                  id="schedule-time"
                  value={form.time_hhmm}
                  placeholder="e.g. 08:00"
                  onChange={(e) => setForm((f) => ({ ...f, time_hhmm: e.target.value }))}
                  data-testid="schedule-time-input"
                />
              </div>
            </div>
            {form.frequency === 'weekly' && (
              <div className="space-y-1.5">
                <Label className="text-[12px]">Day of week</Label>
                <div className="flex gap-1">
                  {DOW.map((d, i) => (
                    <OptionChip
                      key={d}
                      selected={form.day_of_week === i}
                      className="flex-1 px-0 text-[12.5px]"
                      onClick={() => setForm((f) => ({ ...f, day_of_week: i }))}
                      data-testid={`schedule-dow-${i}`}
                    >
                      {d}
                    </OptionChip>
                  ))}
                </div>
              </div>
            )}
            {(form.frequency === 'monthly' || form.frequency === 'quarterly') && (
              <div className="space-y-1.5">
                <Label htmlFor="schedule-dom" className="text-[12px]">Day of month (1–28)</Label>
                <Input
                  id="schedule-dom"
                  type="number"
                  min={1}
                  max={28}
                  value={form.day_of_month}
                  onChange={(e) => setForm((f) => ({ ...f, day_of_month: parseInt(e.target.value, 10) || 1 }))}
                  data-testid="schedule-dom-input"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="schedule-recipients" className="text-[12px]">Recipients (comma-separated emails)</Label>
              <Input
                id="schedule-recipients"
                placeholder="e.g. you@yourrestaurant.co.uk, owner@yourrestaurant.co.uk"
                value={form.recipients}
                onChange={(e) => setForm((f) => ({ ...f, recipients: e.target.value }))}
                data-testid="schedule-recipients-input"
              />
              <p className="text-[12.5px] text-ink-faint">Leave empty for preview-only (no email sent).</p>
            </div>
          </div>
          <DialogFooter className="sm:justify-between">
            <div>
              {editingId && (confirmingDelete ? (
                <Button variant="ghost" className="text-signal-stop hover:text-signal-stop font-semibold" onClick={onDelete} data-testid="schedule-delete-confirm">
                  Delete {form.name}? Yes
                </Button>
              ) : (
                <Button variant="ghost" className="text-signal-stop hover:text-signal-stop" onClick={() => setConfirmingDelete(true)} data-testid="schedule-delete">
                  Delete
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} data-testid="schedule-cancel-btn">Cancel</Button>
            <Button
              className="bg-verlo hover:bg-verlo-hover"
              onClick={onCreate}
              disabled={saving}
              data-testid="schedule-save-btn"
            >
              {saving ? 'Saving…' : (editingId ? 'Save changes' : 'Create schedule')}
            </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </ReportsLayout>
  );
};

export default ReportsSchedulesPage;
